import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { GetRangeHistoryQuery, GetRangeHistoryQueryVariables, QuestMetricType } from '../client/generated/graphql';
import { getRangeHistoryQuery } from '../client/queries/rangeHistory';
import { ListQuest } from '../client/types';
import { getToday } from '../helpers/datetime';
import { dayTimeOptions, mapGraphQLTimeOfDayToTimeOfDay } from '../helpers/quest';
import { useCachedQuestsWithFallback } from './caching/quest';

export type DailyProgress = {
    done: number;
    total: number;
};

export type CompletedQuestSummary = {
    questId: string;
    questName: string;
    xpEarned: number;
    timeSpent: number;
    timeOfDay: string;
    categoryId: string;
};

export type DailyProgressExtended = {
    progressMap: Record<string, DailyProgress>;
    totalXP: number;
    completedQuests: CompletedQuestSummary[];
    allQuestsCompleted: boolean;
};

export const useDailyProgress = (questsGroupBy: Record<string, ListQuest[]>): { data: DailyProgressExtended | null; loading: boolean } => {
    const start = getToday();
    const end = start.add(1, 'day');
    const { data: historyData, loading: queryLoading } = useQuery<GetRangeHistoryQuery, GetRangeHistoryQueryVariables>(
        getRangeHistoryQuery,
        {
            variables: { start, end },
        },
    );

    const logs = historyData?.eventLogs ?? [];

    const questIdsFromLogs = useMemo(() => {
        const ids = new Set<string>();
        logs.forEach(log => {
            log.questMetricIncreased.forEach(metric => {
                if (metric.questId) ids.add(metric.questId);
            });
        });
        return Array.from(ids);
    }, [logs]);

    const { quests, loading: questsLoading } = useCachedQuestsWithFallback(questIdsFromLogs);
    const questMap: Record<string, ListQuest> = Object.fromEntries(quests.map(q => [q.id, q] as [string, ListQuest]));

    const loading = queryLoading || questsLoading;

    const entries = logs.flatMap(log => ({ section: mapGraphQLTimeOfDayToTimeOfDay(log.timeOfDay) }));

    const progressMap = useMemo(() => {
        return dayTimeOptions.reduce((acc, section) => {
            const base = questsGroupBy[section]?.length ?? 0;
            const done = entries.filter(e => e.section === section).length;
            return { ...acc, [section]: { done, total: base + done } };
        }, {} as Record<string, DailyProgress>);
    }, [entries, questsGroupBy]);

    const extendedData = useMemo(() => {
        const totalXP = logs.reduce((sum, log) => {
            const xpMetric = log.questMetricIncreased.find(m => m.questMetricType === QuestMetricType.Experience);
            return sum + (xpMetric?.addedValue ?? 0);
        }, 0);

        const completedQuests: CompletedQuestSummary[] = logs
            .map(log => {
                const xpMetric = log.questMetricIncreased.find(m => m.questMetricType === QuestMetricType.Experience);
                const timeMetric = log.questMetricIncreased.find(m => m.questMetricType === QuestMetricType.Time);
                const questId = xpMetric?.questId ?? '';
                const quest = questMap[questId];
                const timeOfDayValue = mapGraphQLTimeOfDayToTimeOfDay(log.timeOfDay);

                return {
                    questId,
                    questName: quest?.name ?? 'Unknown Quest',
                    xpEarned: xpMetric?.addedValue ?? 0,
                    timeSpent: timeMetric?.addedValue ?? 0,
                    timeOfDay: timeOfDayValue ?? 'Unknown',
                    categoryId: quest?.categoryId ?? '',
                };
            })
            .filter(q => q.questId && q.xpEarned > 0);

        const totalDone = Object.values(progressMap).reduce((sum, p) => sum + p.done, 0);
        const totalQuests = Object.values(progressMap).reduce((sum, p) => sum + p.total, 0);
        const allQuestsCompleted = totalQuests > 0 && totalDone === totalQuests;

        return {
            progressMap,
            totalXP,
            completedQuests,
            allQuestsCompleted,
        };
    }, [logs, progressMap, questMap]);

    return {
        data: loading ? null : extendedData,
        loading,
    };
};
